import { useState } from "react"
import { Lock, Mail, Truck, ArrowRight } from "lucide-react"
import { Button } from "../components/ui/button"
import LanguageSwitcher from "../components/LanguageSwitcher"
import { API_ENDPOINTS } from "../config/api"
import { useLanguage } from "../i18n/LanguageContext"

export default function LoginPage({ onLogin }) {
  const { isRTL, t, translateApiMessage } = useLanguage()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError("")
    setLoading(true)

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      })
      const data = await response.json()

      if (response.ok) {
        onLogin(data.token, data.admin)
      } else {
        setError(translateApiMessage(data.message, "auth.loginFailed"))
      }
    } catch (requestError) {
      setError(t("auth.connectionError"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 flex items-center justify-center p-4 relative overflow-hidden">
      <LanguageSwitcher className="login-language-control" />

      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-amber-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-red-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-white/20">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 mb-4 shadow-xl animate-pulse-slow">
              <Truck className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
              Tout&apos;s Routes
            </h1>
            <p className="text-gray-600 font-medium">{t("auth.adminDashboard")}</p>
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 bg-amber-50 rounded-full border border-amber-200">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-gray-700">{t("auth.systemOnline")}</span>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg animate-shake" role="alert">
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="group">
              <label htmlFor="admin-email" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("auth.emailAddress")}
              </label>
              <div className="relative">
                <div className="form-icon-start absolute inset-y-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="w-5 h-5 text-gray-400 group-focus-within:text-amber-500 transition-colors" />
                </div>
                <input
                  id="admin-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="form-input-with-icon ltr-field w-full py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 transition-all text-gray-900 placeholder-gray-400"
                  placeholder="admin@toutroutes.com"
                  required
                />
              </div>
            </div>

            <div className="group">
              <label htmlFor="admin-password" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("auth.password")}
              </label>
              <div className="relative">
                <div className="form-icon-start absolute inset-y-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-gray-400 group-focus-within:text-amber-500 transition-colors" />
                </div>
                <input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="form-input-with-icon ltr-field w-full py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 transition-all text-gray-900 placeholder-gray-400"
                  placeholder="••••••••••"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-600 hover:via-orange-600 hover:to-red-600 text-white py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  <span>{t("auth.signingIn")}</span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <span>{t("auth.signIn")}</span>
                  <ArrowRight className={`w-5 h-5 ${isRTL ? "rotate-180" : ""}`} />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-600 mb-3">
              {t("auth.helpQuestion")} {" "}
              <a
                href="mailto:toutsroutes@gmail.com"
                className="text-amber-600 hover:text-amber-700 font-semibold hover:underline transition-colors"
              >
                {t("auth.contactSupport")}
              </a>
            </p>
            <p className="text-center text-sm">
              <a href="/privacy-policy" className="font-semibold text-amber-600 hover:text-amber-700 hover:underline transition-colors">
                {t("auth.privacyPolicy")}
              </a>
            </p>
          </div>
        </div>

        <p className="text-center mt-6 text-sm text-gray-600">
          {t("auth.rightsReserved", { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  )
}
